import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AddIcon from "@mui/icons-material/Add";
import TrashIcon from "../../../icons/TrashIcon";
import ScheduleIcon from "@mui/icons-material/Schedule";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import { isValidEmail } from "../../../utils/email";

type Feedback = { type: "success" | "error"; msg: string } | null;

interface Props {
  scheduledDateTime: moment.Moment | null;
  patientName: string;
  emailList: string[];
  newEmail: string;
  emailError: string;
  feedback: Feedback;
  sending: boolean;
  onBack: () => void;
  onNewEmailChange: (value: string) => void;
  onAddEmail: () => void;
  onRemoveEmail: (email: string) => void;
  onConfirm: () => void;
}

const containerSx = { display: "flex", flexDirection: "column", gap: 3 };

const titleSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const subtitleSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary
};

const summaryBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: "12px 16px",
  borderRadius: "10px",
  bgcolor: `${semanticColors.action.primary}14`,
  border: `1px solid ${semanticColors.action.primary}40`
};

const summaryIconSx = {
  color: semanticColors.action.primary,
  fontSize: 20,
  flexShrink: 0
};

const summaryDateSx = {
  ...typography.body.mBold,
  color: semanticColors.text.primary
};

const listLabelSx = {
  ...typography.body.mBold,
  color: semanticColors.text.secondary,
  mb: 1.5
};

const emailListSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  mb: 2
};

const emailRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: "14px",
  py: "10px",
  borderRadius: "10px",
  border: `1px solid ${semanticColors.border.light}`,
  bgcolor: semanticColors.surface.canvas
};

const emailIconSx = {
  fontSize: 18,
  color: semanticColors.action.primary,
  flexShrink: 0
};

const emailTextSx = {
  ...typography.body.l,
  color: semanticColors.text.primary,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const removeBtnSx = {
  color: semanticColors.feedback.danger,
  flexShrink: 0
};

const inputRowSx = { display: "flex", gap: 1 };

const emailInputSx = (hasError: boolean) => ({
  flex: 1,
  height: "44px",
  px: "14px",
  borderRadius: "8px",
  border: `1.5px solid ${
    hasError ? semanticColors.feedback.danger : semanticColors.border.strong
  }`,
  outline: "none",
  ...typography.body.l,
  color: semanticColors.text.primary,
  bgcolor: semanticColors.surface.canvas,
  "&:focus": {
    borderColor: hasError
      ? semanticColors.feedback.danger
      : semanticColors.border.focus
  },
  "&::placeholder": { color: semanticColors.text.subtle }
});

const addBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  height: "44px",
  px: "14px",
  borderRadius: "8px",
  border: `1.5px solid ${semanticColors.action.primary}`,
  bgcolor: "transparent",
  cursor: "pointer",
  color: semanticColors.action.primary,
  flexShrink: 0,
  "&:hover": { bgcolor: `${semanticColors.action.primary}10` }
};

const addLabelSx = { ...typography.body.mBold, color: "inherit" };

const errorTextSx = {
  ...typography.body.s,
  color: semanticColors.feedback.danger,
  mt: "6px"
};

const feedbackBoxSx = (type: "success" | "error") => ({
  p: "12px 16px",
  borderRadius: "10px",
  bgcolor:
    type === "success"
      ? `${semanticColors.feedback.ok}18`
      : `${semanticColors.feedback.danger}18`,
  border: `1px solid ${
    type === "success"
      ? semanticColors.feedback.ok
      : semanticColors.feedback.danger
  }50`
});

const feedbackTextSx = (type: "success" | "error") => ({
  ...typography.body.m,
  color:
    type === "success"
      ? semanticColors.feedback.ok
      : semanticColors.feedback.danger
});

const actionsRowSx = { display: "flex", gap: 1.5 };

const backBtnSx = {
  flex: 1,
  height: "48px",
  borderRadius: "40px",
  border: `1.5px solid ${semanticColors.border.strong}`,
  bgcolor: "transparent",
  cursor: "pointer",
  ...typography.body.lBold,
  color: semanticColors.text.primary,
  "&:hover": { bgcolor: semanticColors.surface.containerLow }
};

const confirmBtnSx = (disabled: boolean) => ({
  flex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  height: "48px",
  borderRadius: "40px",
  border: "none",
  background: semanticColors.gradient.surface,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1
});

const confirmIconSx = { fontSize: 18, color: "#fff" };

const confirmLabelSx = { ...typography.body.lBold, color: "#fff" };

export default function EmailInviteStep({
  scheduledDateTime,
  patientName,
  emailList,
  newEmail,
  emailError,
  feedback,
  sending,
  onBack,
  onNewEmailChange,
  onAddEmail,
  onRemoveEmail,
  onConfirm
}: Props) {
  const { t } = useTranslation();

  return (
    <Box maxWidth={480} sx={containerSx}>
      {/* Title */}
      <Box>
        <Typography sx={titleSx}>{t("videoDialog.emailStepTitle")}</Typography>
        <Typography sx={subtitleSx}>
          {t("videoDialog.emailStepSubtitle")}
        </Typography>
      </Box>

      {/* Appointment summary */}
      <Box sx={summaryBoxSx}>
        <ScheduleIcon sx={summaryIconSx} />
        <Box>
          <Typography sx={summaryDateSx}>
            {moment(scheduledDateTime).format("DD/MM/YYYY")}
          </Typography>
          <Typography sx={subtitleSx}>
            {moment(scheduledDateTime).format("HH:mm")} · {patientName}
          </Typography>
        </Box>
      </Box>

      {/* Email list */}
      <Box>
        <Typography sx={listLabelSx}>
          {t("videoDialog.emailListLabel")}
        </Typography>
        {emailList.length > 0 && (
          <Box sx={emailListSx}>
            {emailList.map(email => (
              <Box key={email} sx={emailRowSx}>
                <EmailOutlinedIcon sx={emailIconSx} />
                <Typography sx={emailTextSx}>{email}</Typography>
                <Tooltip title={t("videoDialog.removeEmail")}>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveEmail(email)}
                    sx={removeBtnSx}
                  >
                    <TrashIcon size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={inputRowSx}>
          <Box
            component="input"
            type="email"
            value={newEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onNewEmailChange(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAddEmail();
              }
            }}
            onBlur={() => {
              // A valid email commits itself to the list above, freeing the
              // field for the next one — no need to click "Add".
              if (isValidEmail(newEmail)) onAddEmail();
            }}
            placeholder={t("videoDialog.emailInputPlaceholder") as string}
            sx={emailInputSx(!!emailError)}
          />
          {/* The first email is a plain fill-in that auto-commits to the list
              above once valid. The "Add" button only shows from the
              second email onwards (i.e. once the list already has entries). */}
          {emailList.length > 0 && (
            <Box component="button" onClick={onAddEmail} sx={addBtnSx}>
              <AddIcon sx={{ fontSize: 18 }} />
              <Typography sx={addLabelSx}>
                {t("videoDialog.addEmail")}
              </Typography>
            </Box>
          )}
        </Box>
        {emailError && <Typography sx={errorTextSx}>{emailError}</Typography>}
      </Box>

      {/* Feedback */}
      {feedback && (
        <Box sx={feedbackBoxSx(feedback.type)}>
          <Typography sx={feedbackTextSx(feedback.type)}>
            {feedback.msg}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Box sx={actionsRowSx}>
        <Box component="button" onClick={onBack} sx={backBtnSx}>
          {t("videoDialog.back")}
        </Box>
        <Box
          component="button"
          onClick={onConfirm}
          disabled={sending || !!feedback}
          sx={confirmBtnSx(sending || !!feedback)}
        >
          {sending ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            <EmailOutlinedIcon sx={confirmIconSx} />
          )}
          <Typography sx={confirmLabelSx}>
            {sending
              ? t("videoDialog.sending")
              : t("videoDialog.confirmAndSend")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
